"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = exports.DrawerSidebarContext = void 0;

var _react = _interopRequireWildcard(require("react"));

var _propTypes = _interopRequireDefault(require("prop-types"));

var _classnames = _interopRequireDefault(require("classnames"));

var _invariant = _interopRequireDefault(require("invariant"));

var _guid = _interopRequireDefault(require("../../utils/helpers/guid/guid"));

var _icon = _interopRequireDefault(require("../icon"));

var _drawer = require("./drawer.style");

var _stickyFooter = _interopRequireDefault(require("../../__internal__/sticky-footer"));

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

function _getRequireWildcardCache() { if (typeof WeakMap !== "function") return null; var cache = new WeakMap(); _getRequireWildcardCache = function () { return cache; }; return cache; }

function _interopRequireWildcard(obj) { if (obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

const DrawerSidebarContext = /*#__PURE__*/_react.default.createContext({});

exports.DrawerSidebarContext = DrawerSidebarContext;

const Drawer = ({
  defaultExpanded = true,
  expanded,
  onChange,
  children,
  expandedWidth = "40%",
  sidebar,
  animationDuration = "400ms",
  backgroundColor,
  title,
  footer,
  showControls,
  height = "100%",
  stickyHeader,
  stickyFooter,
  ...rest
}) => {
  const drawerSidebarContentRef = (0, _react.useRef)();
  const scrollableContentRef = (0, _react.useRef)();
  const isControlled = (0, _react.useRef)(expanded !== undefined);
  const [isOpening, setIsOpening] = (0, _react.useState)(false);
  const [isClosing, setIsClosing] = (0, _react.useState)(false);
  const [isExpanded, setIsExpanded] = (0, _react.useState)(defaultExpanded);
  const timer = (0, _react.useRef)();
  (0, _react.useEffect)(() => {
    const message = "Drawer should not switch from uncontrolled to controlled" + " (or vice versa). Decide between using a controlled or uncontrolled Drawer element" + " for the lifetime of the component";
    (0, _invariant.default)(isControlled.current === (expanded !== undefined), message);

    if (expanded !== undefined) {
      setIsExpanded(expanded);
    }
  }, [expanded]);
  (0, _react.useEffect)(() => {
    return function cleanup() {
      clearTimeout(timer.current);
    };
  }, []);
  const getAnimationDuration = (0, _react.useCallback)(() => {
    if (animationDuration.indexOf("ms") !== -1) {
      const animationTime = animationDuration.substring(0, animationDuration.length - 2);
      return animationTime;
    }

    if (animationDuration.indexOf(".") !== -1 || animationDuration.indexOf("s") !== -1) {
      const animationTime = animationDuration.substring(0, animationDuration.length - 1);
      return parseFloat(animationTime) * 1000;
    }

    return animationDuration;
  }, [animationDuration]);
  const toggleAnimation = (0, _react.useCallback)(() => {
    const timeout = getAnimationDuration();
    clearTimeout(timer.current);

    if (!isExpanded) {
      setIsClosing(false);
      setIsOpening(true);
      timer.current = setTimeout(() => {
        setIsOpening(false);
      }, timeout);
    } else {
      setIsOpening(false);
      setIsClosing(true);
      timer.current = setTimeout(() => {
        setIsClosing(false);
      }, timeout);
    }
  }, [getAnimationDuration, isExpanded]);
  const toggleDrawer = (0, _react.useCallback)(ev => {
    setIsExpanded(!isExpanded);
    if (onChange) onChange(ev, !isExpanded);

    if (isExpanded) {
      drawerSidebarContentRef.current.scrollTop = 0;
    }

    toggleAnimation();
  }, [toggleAnimation, isExpanded, onChange]);
  const guid = (0, _react.useRef)((0, _guid.default)());
  const sidebarId = `DrawerSidebar_${guid.current}`;
  const getClassNames = (0, _react.useCallback)(() => {
    return (0, _classnames.default)(isExpanded ? "open" : "closed", isOpening ? "opening" : "", isClosing ? "closing" : "");
  }, [isExpanded, isOpening, isClosing]);

  const getControls = () => {
    if (showControls === undefined) return null;
    return /*#__PURE__*/_react.default.createElement(_drawer.StyledButton, {
      "aria-label": "toggle sidebar",
      "aria-expanded": isExpanded,
      "aria-controls": sidebarId,
      "data-element": "drawer-toggle",
      onClick: toggleDrawer,
      isExpanded: isExpanded,
      animationDuration: animationDuration,
      stickyHeader: stickyHeader
    }, /*#__PURE__*/_react.default.createElement(_icon.default, {
      type: "chevron_right"
    }));
  };

  return /*#__PURE__*/_react.default.createElement(_drawer.StyledDrawerWrapper, _extends({
    "data-component": "drawer",
    height: height
  }, rest), /*#__PURE__*/_react.default.createElement(_drawer.StyledDrawerContent, {
    expandedWidth: expandedWidth,
    animationDuration: animationDuration,
    className: getClassNames(),
    "aria-expanded": isExpanded ? "true" : "false",
    ref: drawerSidebarContentRef,
    backgroundColor: backgroundColor
  }, stickyHeader && /*#__PURE__*/_react.default.createElement(_drawer.StyledSidebarHeader, {
    isExpanded: isExpanded
  }, title && /*#__PURE__*/_react.default.createElement(_drawer.StyledSidebarTitle, null, title), getControls()), !stickyHeader && /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, title && /*#__PURE__*/_react.default.createElement(_drawer.StyledSidebarTitle, null, title), getControls()), /*#__PURE__*/_react.default.createElement(_drawer.StyledDrawerSidebar, {
    hasControls: !!showControls,
    id: sidebarId,
    isExpanded: isExpanded,
    role: "navigation",
    overflowY: "auto",
    scrollVariant: "light",
    ref: scrollableContentRef
  }, /*#__PURE__*/_react.default.createElement(DrawerSidebarContext.Provider, {
    value: {
      isInSidebar: true
    }
  }, sidebar), footer && /*#__PURE__*/_react.default.createElement(_stickyFooter.default, {
    containerRef: scrollableContentRef,
    disableSticky: !stickyFooter,
    isExpanded: isExpanded
  }, footer))), /*#__PURE__*/_react.default.createElement(_drawer.StyledDrawerChildren, null, children));
};

Drawer.propTypes = {
  children: _propTypes.default.node.isRequired,

  /** Set the default state of expansion of the Drawer if component is meant to be used as uncontrolled */
  defaultExpanded: _propTypes.default.bool,

  /** Sets the expansion state of the Drawer if component is meant to be used as controlled */
  expanded: _propTypes.default.bool,

  /** Callback fired when expansion state changes, onChange(event: object, isExpanded: boolean) */
  onChange: _propTypes.default.func,

  /* Sidebar object either html or react component */
  sidebar: _propTypes.default.node,

  /* The (% or px) width of the expanded sidebar  */
  expandedWidth: _propTypes.default.string,

  /** Duration of a animation */
  animationDuration: _propTypes.default.string,

  /** Sets color of sidebar's background */
  backgroundColor: _propTypes.default.string,

  /** Sets custom height to Drawer component */
  height: _propTypes.default.string,

  /** Sets the heading of the drawer */
  title: _propTypes.default.node,

  /** Enables expand/collapse button that controls drawer */
  showControls: _propTypes.default.bool,

  /** Content to display inside of a footer */
  footer: _propTypes.default.node,

  /** Makes the header of the drawer sticky. Title prop must also be set. */
  stickyHeader: _propTypes.default.bool,

  /** Makes the footer of the drawer sticky. Footer prop must also be set. */
  stickyFooter: _propTypes.default.bool
};
var _default = Drawer;
exports.default = _default;